<!--
  Tax Edit Modal - for creating/editing single tax entries
  2025-12-15: Added ledger account assignment for ledger-enabled tenants
  2025-10-28: Created for Taxes 2.0 feature
  2025-11-02: Added filterType prop to show only relevant tax types (fees vs taxes)
  2025-11-02: Redesigned UI with card-based sections, improved organization and UX
-->
<template>
  <v-card class="tax-edit-modal" max-width="1000">
    <!-- Header -->
    <v-card-title class="pa-6 primary--text">
      <h2 class="text-h5 font-weight-medium">
        {{ modalTitle }}
      </h2>
      <v-spacer />
      <v-btn icon @click="close">
        <v-icon>close</v-icon>
      </v-btn>
    </v-card-title>

    <v-divider />

    <v-form ref="editTaxForm" lazy-validation @submit.prevent="saveTax">
      <v-card-text class="pa-6">
        <!-- Section 1: Basic Information -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-medium pa-4 pb-2">
            {{ $t('Basic Information') }}
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="taxData.tax_type"
                  outlined
                  dense
                  :items="availableTaxTypes"
                  item-text="text"
                  item-value="value"
                  :label="
                    filterType === 'taxes' ? $t('Tax Type') : $t('Fee Type')
                  "
                  :rules="[required]"
                  @change="onTaxTypeChange"
                />
                <!-- Channel Mappings -->
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="taxData.name"
                  outlined
                  dense
                  :label="$t('Display Name')"
                  :rules="[required]"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="taxData.units"
                  outlined
                  dense
                  :items="getTaxUnits"
                  item-text="text"
                  item-value="value"
                  :label="$t('Unit Type')"
                  :rules="[required]"
                  @change="onUnitChange"
                />
              </v-col>
              <v-col v-if="taxData.units !== 'formula'" cols="12" md="4">
                <v-text-field
                  v-if="taxData.units !== 'listing_based'"
                  v-model="taxData.amount"
                  type="number"
                  outlined
                  dense
                  :label="$t('Amount')"
                  :rules="[requiredAmount]"
                />
                <v-text-field
                  v-else
                  :value="$t('From Listing')"
                  outlined
                  dense
                  :label="$t('Amount')"
                  disabled
                  :hint="$t('Value taken from listing settings')"
                  persistent-hint
                />
              </v-col>
              <v-col
                v-if="
                  taxData.units === 'fixed' || taxData.units === 'listing_based'
                "
                cols="12"
                md="4"
              >
                <v-select
                  v-model="taxData.quantifier"
                  outlined
                  dense
                  :items="taxQuantifierItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('Quantifier')"
                  :rules="[required]"
                />
              </v-col>
              <v-col v-if="taxData.units === 'percentage'" cols="12" md="4">
                <v-select
                  v-model="taxData.applicable_to"
                  outlined
                  dense
                  multiple
                  small-chips
                  :items="taxApplicableTo"
                  item-text="text"
                  item-value="value"
                  :label="$t('Apply To')"
                  :rules="[required]"
                />
              </v-col>
            </v-row>

            <v-row v-if="taxData.units === 'formula'">
              <v-col cols="12">
                <enhanced-formula-builder
                  v-model="taxData.formula"
                  :label="$t('Formula')"
                  :variables="formulaVariables"
                  :rules="[required]"
                  dense
                />
              </v-col>
            </v-row>
            <div v-if="hasChannelMappings" class="channel-mappings mt-2">
              <div class="text-caption grey--text mb-1">
                {{
                  $t(
                    'Those fees will be mapped to the following channels as follows:'
                  )
                }}
              </div>
              <div v-if="channelMappings.airbnb" class="channel-mapping-item">
                <span class="channel-dot airbnb-dot" />
                <span class="text-caption">
                  <span class="font-weight-medium">Airbnb:</span>
                  {{ channelMappings.airbnb }}
                </span>
              </div>
              <div v-if="channelMappings.booking" class="channel-mapping-item">
                <span class="channel-dot booking-dot" />
                <span class="text-caption">
                  <span class="font-weight-medium">Booking.com:</span>
                  {{ channelMappings.booking }}
                </span>
              </div>
              <div v-if="channelMappings.vrbo" class="channel-mapping-item">
                <span class="channel-dot vrbo-dot" />
                <span class="text-caption">
                  <span class="font-weight-medium">Vrbo:</span>
                  {{ channelMappings.vrbo }}
                </span>
              </div>
              <div v-if="channelMappings.direct" class="channel-mapping-item">
                <span class="channel-dot direct-dot" />
                <span class="text-caption">
                  <span class="font-weight-medium">{{ $t('Direct') }}:</span>
                  {{ channelMappings.direct }}
                </span>
              </div>
            </div>

            <!-- Inclusive Fee (Fees only) -->
            <v-row
              v-if="
                taxData.tax_type &&
                !taxData.tax_type.includes('tax') &&
                taxData.tax_type !== 'VAT'
              "
            >
              <v-col cols="12">
                <v-checkbox
                  v-model="taxData.inclusive_fee"
                  class="mt-0"
                  @change="onInclusiveChange"
                  hide-details
                >
                  <template #label>
                    <div class="d-flex align-center">
                      <div class="font-weight-medium mr-2">
                        {{ $t('Inclusive Fee') }}
                      </div>
                      <v-tooltip top max-width="300">
                        <template #activator="{ on, attrs }">
                          <v-icon small color="grey" v-bind="attrs" v-on="on">
                            info
                          </v-icon>
                        </template>
                        <span>
                          {{
                            $t(
                              'This fee will be part of the nightly rate. By using this option, this item will not be pushed to any channel.'
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section: Seasonal Dates (NextPax only) -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-medium pa-4 pb-2">
            {{ $t('Seasonal Dates') }}
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="taxData.from_date"
                  type="date"
                  outlined
                  dense
                  clearable
                  :label="$t('From Date')"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="taxData.until_date"
                  type="date"
                  outlined
                  dense
                  clearable
                  :label="$t('Until Date')"
                />
              </v-col>
            </v-row>
            <v-alert
              v-if="!taxData.from_date && !taxData.until_date"
              type="info"
              text
              dense
              class="mb-0"
            >
              {{
                $t('No seasonal dates set. This item will apply year-round.')
              }}
            </v-alert>
            <div class="text-caption grey--text mt-2">
              {{
                $t(
                  `Seasonal dates will be applied on ${
                    isNextpax ? 'Booking.com, ' : ''
                  } manual and direct reservations
                  only`
                )
              }}
            </div>
          </v-card-text>
        </v-card>

        <!-- Section: Invoice Display -->
        <v-card outlined class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-medium pa-4 pb-2">
            Invoice Display
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-checkbox
              v-model="taxData.invoiceable"
              class="mt-0"
              hide-details="auto"
            >
              <template #label>
                <div class="d-flex align-center">
                  <div class="font-weight-medium mr-2">Show on Invoice</div>
                  <v-tooltip top max-width="300">
                    <template #activator="{ on, attrs }">
                      <v-icon small color="grey" v-bind="attrs" v-on="on">
                        info
                      </v-icon>
                    </template>
                    <span>
                      {{
                        taxData.tax_type && taxData.tax_type.includes('tax')
                          ? 'When unchecked, this tax will be included in the accommodation fare but not shown as a separate line item'
                          : 'When unchecked, this fee will be included in the accommodation fare but not shown as a separate line item'
                      }}
                    </span>
                  </v-tooltip>
                </div>
              </template>
            </v-checkbox>
          </v-card-text>
        </v-card>

        <!-- Section 2: Application Scope -->
        <v-card
          outlined
          class="mb-4"
          :class="{ 'disabled-section': isPostBooking }"
        >
          <v-card-title class="text-subtitle-1 font-weight-medium pa-4 pb-2">
            {{ $t('Application Scope') }}
            <v-chip
              v-if="isPostBooking"
              small
              class="ml-2"
              color="grey lighten-2"
            >
              {{ $t('N/A - Post-Booking') }}
            </v-chip>
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12">
                <v-radio-group
                  v-model="applicationScope"
                  class="mt-0"
                  hide-details
                  @change="applicationScopeChange"
                  :disabled="isPostBooking"
                >
                  <v-radio value="both">
                    <template #label>
                      <div>
                        <div class="font-weight-medium">
                          {{ $t('Both OTA and Direct/Manual Reservations') }}
                        </div>
                        <div class="text-caption grey--text">
                          {{ $t('Apply to all reservation types') }}
                        </div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="direct">
                    <template #label>
                      <div>
                        <div class="font-weight-medium">
                          {{ $t('Direct/Manual Reservations Only') }}
                        </div>
                        <div class="text-caption grey--text">
                          {{
                            $t(
                              'Apply only to direct bookings, not channel bookings'
                            )
                          }}
                        </div>
                      </div>
                    </template>
                  </v-radio>
                  <v-radio value="ota">
                    <template #label>
                      <div>
                        <div class="font-weight-medium">
                          {{ $t('OTA Reservations Only') }}
                        </div>
                        <div class="text-caption grey--text">
                          {{
                            $t(
                              'Apply only to channel bookings (Airbnb, Booking.com, etc.), not direct bookings'
                            )
                          }}
                        </div>
                      </div>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-col>
              <v-col cols="12">
                <v-checkbox
                  v-model="taxData.include_in_nightly_rate"
                  :disabled="
                    !(
                      taxData.tax_type &&
                      !taxData.inclusive_fee &&
                      applicationScope !== 'ota'
                    )
                  "
                  class="mt-0"
                  hide-details
                >
                  <template #label>
                    <div class="d-flex align-center">
                      <div class="font-weight-medium mr-2">
                        {{ $t('Hidden Fee') }}
                      </div>
                      <v-tooltip top max-width="300">
                        <template #activator="{ on, attrs }">
                          <v-icon small color="grey" v-bind="attrs" v-on="on">
                            info
                          </v-icon>
                        </template>
                        <span>
                          {{
                            $t(
                              'When enabled, this fee will be added to the nightly rate and hidden from the guest breakdown on your booking site. The fee is still tracked separately for accounting purposes.'
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>

            <v-expand-transition>
              <v-row v-if="applicationScope === 'direct'" class="mt-4">
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="taxData.minimum_amount"
                    type="number"
                    min="0"
                    step="0.01"
                    outlined
                    dense
                    :label="$t('Minimum Total Price')"
                    :hint="$t('Apply only if total price is above this amount')"
                    persistent-hint
                  />
                </v-col>
                <v-col
                  v-if="
                    taxData.tax_type === 'VAT' ||
                    (taxData.tax_type && taxData.tax_type.includes('tax'))
                  "
                  cols="12"
                  md="6"
                >
                  <v-text-field
                    v-model="taxData.max_days"
                    type="number"
                    outlined
                    dense
                    :label="$t('Maximum Days')"
                    :hint="$t('Apply only for stays up to this many days')"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </v-expand-transition>
          </v-card-text>
        </v-card>

        <!-- Section 3: Special Options (VAT only) -->
        <v-card v-if="taxData.tax_type === 'VAT'" outlined class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-medium pa-4 pb-2">
            {{ $t('Special Options') }}
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-row>
              <!-- VAT Inclusive -->
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="taxData.inclusive"
                  class="mt-0"
                  hide-details
                >
                  <template #label>
                    <div>
                      <div class="font-weight-medium">
                        {{ $t('Price Inclusive VAT') }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ $t('VAT is already included in the listed price') }}
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>

              <!-- Local Citizens Only -->
              <v-col cols="12" md="6">
                <v-checkbox
                  v-model="taxData.is_local"
                  class="mt-0"
                  hide-details
                >
                  <template #label>
                    <div>
                      <div class="font-weight-medium">
                        {{ $t('Local Citizens Only') }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ $t('Apply only to local residents') }}
                      </div>
                    </div>
                  </template>
                </v-checkbox>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Section 4: Fee Income Assignment (Fees only) -->
        <v-card
          v-if="
            taxData.tax_type &&
            !taxData.tax_type.toLowerCase().includes('tax') &&
            taxData.tax_type !== 'VAT'
          "
          outlined
          class="mb-4"
        >
          <v-card-title class="text-subtitle-1 font-weight-medium pa-4 pb-2">
            {{ $t('Income Assignment') }}
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-radio-group
              v-model="taxData.tax_relation"
              class="mt-0"
              hide-details
              row
            >
              <v-radio value="owner" class="mr-8">
                <template #label>
                  <div>
                    <div class="font-weight-medium">
                      {{ $t('Owner Income') }}
                    </div>
                    <div class="text-caption grey--text">
                      {{ $t('Fee goes to property owner') }}
                    </div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="pmc">
                <template #label>
                  <div>
                    <div class="font-weight-medium">{{ $t('PMC Income') }}</div>
                    <div class="text-caption grey--text">
                      {{ $t('Fee goes to property management company') }}
                    </div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </v-card-text>
        </v-card>

        <!-- Section 5: Ledger Account Assignment (when ledger is enabled) -->
        <v-card v-if="ledgerEnabled" outlined class="mb-4">
          <v-card-title class="text-subtitle-1 font-weight-medium pa-4 pb-2">
            <v-icon small class="mr-2">account_balance</v-icon>
            {{ $t('Ledger Account Assignment') }}
          </v-card-title>
          <v-divider />
          <v-card-text class="pa-4">
            <v-row>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="ledgerAccountId"
                  outlined
                  dense
                  clearable
                  :items="ledgerAccountItems"
                  item-text="display"
                  item-value="id"
                  :label="$t('Ledger Account')"
                  :hint="
                    $t(
                      'Select account or leave empty for default unmatched account'
                    )
                  "
                  persistent-hint
                >
                  <template #item="{ item }">
                    <div class="d-flex align-center">
                      <span class="font-weight-medium mr-2">{{
                        item.code
                      }}</span>
                      <span class="grey--text text--darken-1">{{
                        item.name
                      }}</span>
                    </div>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <v-select
                  v-model="recognisedAt"
                  outlined
                  dense
                  :items="recognisedAtItems"
                  item-text="text"
                  item-value="value"
                  :label="$t('Revenue Recognition')"
                  :hint="$t('When to recognize revenue in the ledger')"
                  persistent-hint
                />
              </v-col>
            </v-row>
            <v-alert
              v-if="!ledgerAccountId"
              type="info"
              text
              dense
              class="mt-3 mb-0"
            >
              {{
                $t(
                  'No account selected. This item will be assigned to the default unmatched account (9900 for taxes, 9910 for fees).'
                )
              }}
            </v-alert>
          </v-card-text>
        </v-card>

        <!-- Warning Alert -->
        <v-alert
          v-if="
            taxData.internal ||
            taxData.ota_only ||
            taxData.inclusive ||
            taxData.inclusive_fee ||
            taxData.include_in_nightly_rate
          "
          type="warning"
          text
          dense
          icon="warning"
          class="mb-0"
        >
          <div class="d-flex align-center">
            <div class="text-body-2">
              <span v-if="taxData.include_in_nightly_rate">
                {{
                  $t(
                    'This fee will be added to the displayed nightly rate and hidden from the guest breakdown on your booking site. It will still be tracked separately for accounting.'
                  )
                }}
              </span>
              <span v-else-if="taxData.internal">
                {{
                  $t(
                    'This configuration will keep this item as internal only and prevent it from being pushed to booking channels.'
                  )
                }}
              </span>
              <span v-else-if="taxData.ota_only">
                {{
                  $t(
                    'This configuration will apply only to OTA reservations and will not be included in direct/manual bookings.'
                  )
                }}
              </span>
              <span v-else>
                {{
                  $t(
                    'This configuration will keep this item as internal only and prevent it from being pushed to booking channels.'
                  )
                }}
              </span>
            </div>
          </div>
        </v-alert>
      </v-card-text>

      <!-- Footer Actions -->
      <v-divider />
      <v-card-actions class="pa-4 justify-end">
        <v-btn text class="px-6" @click="close">
          {{ $t('Cancel') }}
        </v-btn>
        <v-btn color="primary" depressed type="submit" class="px-6">
          {{ $t('Save') }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import FormRules from 'components/mixins/form-rules-mixin'
import FormattersMixin from 'components/mixins/formatters-mixin'
import EnhancedFormulaBuilder from 'components/common/enhanced-formula-builder'
import { mapActions, mapState } from 'vuex'

// Channel mapping constants for Airbnb and Booking.com
const AIRBNB_MAPPING = {
  // Taxes
  'local tax': 'Lodging Tax',
  VAT: 'VAT/GST',
  'tourist tax': 'Tourist Tax',
  'city tax': 'Sales Tax',
  'goods and services tax': 'VAT/GST',
  // Fees
  'Air conditioning fee': 'Air Conditioning Fee',
  'Airport shuttle fee': 'Resort Fee',
  'Bed linen fee': 'Linen Fee',
  'Book Direct service fee': 'Management Fee',
  'Booking fee': 'Management Fee',
  'Children extra fee': 'Resort Fee',
  'Club card fee': 'Resort Fee',
  'Conservation fee': 'Community Fee',
  'Credit card fee': 'Management Fee',
  'Damage Waiver fee': 'Management Fee',
  'Destination fee': 'Community Fee',
  'Electricity fee': 'Electricity Fee',
  'Environment fee': 'Community Fee',
  'Extra bed fee': 'Resort Fee',
  'Gas fee': 'Utility Fee',
  'Heating fee': 'Heating Fee',
  'Housekeeping fee': 'Resort Fee',
  'Internet fee': 'Resort Fee',
  'Kitchen linen fee': 'Linen Fee',
  'Linen package fee': 'Linen Fee',
  'Oil fee': 'Utility Fee',
  'Parking fee': 'Resort Fee',
  'Pet fee': 'Pet Fee',
  'Resort fee': 'Resort Fee',
  'Sea plane fee': 'Resort Fee',
  'Service fee': 'Management Fee',
  'Shuttle boat fee': 'Resort Fee',
  'Ski pass': 'Resort Fee',
  'Tourism fee': 'Tourism Assessment Fee',
  'Towels fee': 'Linen Fee',
  'Transfer fee': 'Resort Fee',
  'Visa support fee': 'Resort Fee',
  'Water park fee': 'Resort Fee',
  'Water usage fee': 'Water Fee',
  'Wood fee': 'Utility Fee',
  'Wristband fee': 'Resort Fee',
  'Airbnb Short term cleaning fee': 'Short Term Cleaning Fee',
}

const BOOKING_MAPPING = {
  // Taxes
  'city tax': 'City tax',
  'goods and services tax': 'Goods & services tax',
  'local tax': 'Local council tax',
  'tourist tax': 'Government tax',
  VAT: 'VAT',
  // Fees
  'Air conditioning fee': 'Air conditioning fee',
  'Airport shuttle fee': 'Airport shuttle fee',
  'Bed linen fee': 'Bed linens fee',
  'Booking fee': 'Service charge',
  'Cleaning fee': 'Cleaning fee',
  'Club card fee': 'Loyalty card fee',
  'Conservation fee': 'Conservation fee',
  'Credit card fee': 'Credit card fee',
  'Destination fee': 'Destination charge',
  'Electricity fee': 'Electricity fee',
  'Environment fee': 'Environment fee',
  'Extra bed fee': 'Extra bed for adult',
  'Gas fee': 'Gas fee',
  'Heating fee': 'Heating fee',
  'Housekeeping fee': 'Housekeeping fee',
  Insurance: 'Insurance fee',
  'Internet fee': 'Internet fee',
  'Kitchen linen fee': 'Kitchen linen fee',
  'Linen package fee': 'Linen package fee',
  'Oil fee': 'Oil fee',
  'Parking fee': 'Parking fee',
  'Pet fee': 'Pet fee',
  'Resort fee': 'Resort fee',
  'Sea plane fee': 'Sea plane fee',
  'Service fee': 'Service charge',
  'Shuttle boat fee': 'Shuttle boat fee',
  'Ski pass': 'Ski pass',
  'Tourism fee': 'Tourism fee',
  'Towels fee': 'Towel charge',
  'Transfer fee': 'Transfer fee',
  'Visa support fee': 'Visa support fee',
  'Water park fee': 'Waterpark fee',
  'Water usage fee': 'Water usage fee',
  'Wood fee': 'Wood fee',
  'Wristband fee': 'Wristband fee',
}

const VRBO_MAPPING = {
  // Taxes (all map to Administrative)
  'local tax': 'Administrative',
  VAT: 'Administrative',
  'tourist tax': 'Administrative',
  'goods and services tax': 'Administrative',
  'city tax': 'Administrative',
  // Fees
  'Air conditioning fee': 'Air-conditioning',
  'Airport shuttle fee': 'Transportation',
  'Bed linen fee': 'Linens (bed)',
  'Booking fee': 'Booking',
  'Children extra fee': 'Management',
  'Club card fee': 'Club',
  'Conservation fee': 'Consumption',
  'Credit card fee': 'On-site payment method',
  'Damage Waiver fee': 'Waiver damage',
  'Destination fee': 'Administrative',
  'Electricity fee': 'Electricity',
  'Environment fee': 'Administrative',
  'Extra bed fee': 'Additional bed',
  'Gas fee': 'Gas',
  'Heating fee': 'Heating',
  'Housekeeping fee': 'Daily cleaning',
  'Internet fee': 'Internet',
  'Kitchen linen fee': 'Linens',
  'Linen package fee': 'Linens',
  'Oil fee': 'Oil',
  'Parking fee': 'Parking',
  'Pet fee': 'Pet',
  'Resort fee': 'Resort',
  'Sea plane fee': 'Transportation',
  'Service fee': 'Administrative',
  'Shuttle boat fee': 'Transportation',
  'Ski pass': 'Consumption',
  'Tourism fee': 'Administrative',
  'Towels fee': 'Linens (bath)',
  'Transfer fee': 'Transportation',
  'Visa support fee': 'On-site payment method',
  'Water park fee': 'Consumption',
  'Water usage fee': 'Water',
  'Wood fee': 'Wood',
  'Wristband fee': 'Consumption',
}

export default {
  name: 'TaxEditModal',
  mixins: [FormRules, FormattersMixin],
  components: {
    EnhancedFormulaBuilder,
  },
  props: {
    tax: {
      type: Object,
      default: null,
    },
    filterType: {
      type: String,
      default: 'all',
      validator: value => ['all', 'fees', 'taxes'].includes(value),
    },
  },
  data() {
    return {
      taxData: {
        tax_type: null,
        name: null,
        units: 'fixed',
        amount: 0,
        quantifier: 'per_stay',
        applicable_to: ['AF'], // Default to Accommodation Fare only
        tax_relation: 'pmc',
        inclusive: false,
        inclusive_fee: false,
        include_in_nightly_rate: false,
        invoiceable: true,
        minimum_amount: null,
        max_days: null,
        is_local: false,
        internal: false,
        ota_only: false,
        formula: null,
        listing_based: false,
        listing_field_path: null,
        optional: false,
        post_booking: false,
        from_date: null,
        until_date: null,
      },
      // Ledger account assignment fields
      ledgerAccountId: null,
      recognisedAt: 'nightly',
    }
  },
  computed: {
    ...mapState('taxes', [
      'taxTypes',
      'taxQuantifier',
      'listingBasedFees',
      'applicableToOptions',
      'ledgerEnabled',
      'ledgerAccounts',
      'recognisedAtOptions',
    ]),
    modalTitle() {
      const isEdit = !!this.tax
      if (this.filterType === 'fees') {
        return isEdit ? this.$t('Edit Fee') : this.$t('Add Fee')
      } else if (this.filterType === 'taxes') {
        return isEdit ? this.$t('Edit Tax') : this.$t('Add Tax')
      }
      return isEdit ? this.$t('Edit Tax/Fee') : this.$t('Add Tax/Fee')
    },
    availableTaxTypes() {
      if (!this.taxTypes || typeof this.taxTypes !== 'object') return []

      // Define actual tax types (not fees)
      const actualTaxTypes = [
        'local tax',
        'VAT',
        'tourist tax',
        'goods and services tax',
        'city tax',
      ]

      let allTypes = Object.keys(this.taxTypes).map(key => ({
        text: key,
        value: key,
      }))

      // Filter based on filterType prop
      if (this.filterType === 'taxes') {
        // Show only actual tax types
        return allTypes.filter(type => actualTaxTypes.includes(type.value))
      } else if (this.filterType === 'fees') {
        // Show only fee types (exclude actual taxes)
        return allTypes.filter(type => !actualTaxTypes.includes(type.value))
      }

      // Return all types if filterType is 'all'
      return allTypes
    },
    taxQuantifierItems() {
      if (!this.taxQuantifier || typeof this.taxQuantifier !== 'object')
        return []
      return Object.entries(this.taxQuantifier).map(([key, value]) => ({
        text: value,
        value: key,
      }))
    },
    getTaxUnits() {
      const baseUnits = [
        { text: this.currencySign, value: 'fixed' },
        { text: '%', value: 'percentage' },
        { text: this.$t('Formula'), value: 'formula' },
      ]

      if (this.taxData.tax_type && this.isListingBased(this.taxData.tax_type)) {
        baseUnits.push({
          text: this.$t('Listing Based'),
          value: 'listing_based',
        })
      }

      return baseUnits
    },
    taxApplicableTo() {
      // Use dynamic fees from database, always includes Accommodation Fare and Cleaning Fee
      if (this.applicableToOptions && Array.isArray(this.applicableToOptions)) {
        return this.applicableToOptions
      }

      // Fallback to Accommodation Fare and Cleaning Fee if options not loaded yet
      return [
        { text: this.$t('Accommodation Fare'), value: 'AF' },
        { text: this.$t('Cleaning fee'), value: 'CF' },
      ]
    },
    formulaVariables() {
      return [
        {
          value: 'nights_count',
          description: this.$t('Number of nights in the reservation'),
          category: this.$t('Reservation'),
        },
        {
          value: 'guest_count',
          description: this.$t('Total number of guests'),
          category: this.$t('Reservation'),
        },
        {
          value: 'adult_count',
          description: this.$t('Number of adult guests'),
          category: this.$t('Reservation'),
        },
      ]
    },
    isPostBooking() {
      return this.taxData.post_booking === true
    },
    channelMappings() {
      if (!this.taxData.tax_type)
        return { direct: null, airbnb: null, booking: null, vrbo: null }
      return {
        direct: this.taxData.name || null,
        airbnb: AIRBNB_MAPPING[this.taxData.tax_type] || null,
        booking: BOOKING_MAPPING[this.taxData.tax_type] || null,
        vrbo: VRBO_MAPPING[this.taxData.tax_type] || null,
      }
    },
    hasChannelMappings() {
      return (
        this.channelMappings.direct ||
        this.channelMappings.airbnb ||
        this.channelMappings.booking ||
        this.channelMappings.vrbo
      )
    },
    ledgerAccountItems() {
      if (!this.ledgerAccounts || !Array.isArray(this.ledgerAccounts)) {
        return []
      }
      return this.ledgerAccounts.map(account => ({
        ...account,
        display: `${account.code} - ${account.name}`,
      }))
    },
    recognisedAtItems() {
      const options = this.recognisedAtOptions || [
        'confirmed_at',
        'check_in',
        'check_out',
        'nightly',
      ]
      const labels = {
        confirmed_at: this.$t('When Confirmed'),
        check_in: this.$t('At Check-in'),
        check_out: this.$t('At Check-out'),
        nightly: this.$t('Nightly (spread across stay)'),
      }
      return options.map(opt => ({
        value: opt,
        text: labels[opt] || opt,
      }))
    },
    requiredAmount() {
      return value => {
        if (
          this.taxData.units === 'formula' ||
          this.taxData.units === 'listing_based'
        ) {
          return true
        }
        return !!value || value === 0 || this.$t('Amount is required')
      }
    },
    applicationScope: {
      get() {
        if (this.taxData.internal) {
          return 'direct'
        } else if (this.taxData.ota_only) {
          return 'ota'
        }
        return 'both'
      },
      set(value) {
        if (value === 'direct') {
          this.taxData.internal = true
          this.taxData.ota_only = false
        } else if (value === 'ota') {
          this.taxData.internal = false
          this.taxData.ota_only = true
        } else {
          // 'both'
          this.taxData.internal = false
          this.taxData.ota_only = false
        }
      },
    },
  },
  created() {
    if (this.tax) {
      this.taxData = { ...this.tax }
      // Load existing ledger account line data if available
      if (this.tax.ledger_account_line) {
        this.ledgerAccountId = this.tax.ledger_account_line.ledger_account_id
        this.recognisedAt =
          this.tax.ledger_account_line.recognised_at || 'nightly'
      }
    }
  },
  methods: {
    ...mapActions('taxes', ['createTax', 'updateTax']),
    isListingBased(taxType) {
      return this.listingBasedFees && this.listingBasedFees[taxType]
    },
    onUnitChange() {
      switch (this.taxData.units) {
        case 'listing_based':
          this.taxData.listing_based = true
          this.taxData.listing_field_path =
            this.listingBasedFees[this.taxData.tax_type]
          this.taxData.amount = null
          this.taxData.formula = null
          if (!this.taxData.quantifier) {
            this.taxData.quantifier = 'per_stay'
          }
          break

        case 'formula':
          this.taxData.listing_based = false
          this.taxData.listing_field_path = null
          this.taxData.amount = null
          if (!this.taxData.formula) {
            this.taxData.formula = ''
          }
          break

        case 'fixed':
        case 'percentage':
          this.taxData.listing_based = false
          this.taxData.listing_field_path = null
          this.taxData.formula = null
          if (
            this.taxData.amount === null ||
            this.taxData.amount === undefined
          ) {
            this.taxData.amount = 0
          }
          if (this.taxData.units === 'fixed' && !this.taxData.quantifier) {
            this.taxData.quantifier = 'per_stay'
          }
          break
      }
    },
    onTaxTypeChange() {
      if (this.isListingBased(this.taxData.tax_type)) {
        this.taxData.listing_field_path =
          this.listingBasedFees[this.taxData.tax_type]
        if (!this.taxData.units) {
          this.taxData.units = 'fixed'
        }
      } else {
        this.taxData.listing_based = false
        this.taxData.listing_field_path = null
        if (this.taxData.units === 'listing_based') {
          this.taxData.units = 'fixed'
        }
      }
    },
    onInclusiveChange(val) {
      if (val) {
        this.taxData.include_in_nightly_rate = false
      }
    },
    applicationScopeChange(val) {
      if (val) {
        if (val === 'ota') {
          this.taxData.include_in_nightly_rate = false
        }
      }
    },
    close() {
      this.$store.commit('hideModal')
    },
    async saveTax() {
      if (this.$refs.editTaxForm.validate()) {
        this.$store.commit('updateLoading', true)
        try {
          // Build payload with ledger params if ledger is enabled
          const payload = {
            tax: this.taxData,
          }
          if (this.ledgerEnabled) {
            payload.ledger_account_id = this.ledgerAccountId
            payload.recognised_at = this.recognisedAt
          }

          if (this.tax) {
            await this.updateTax({
              id: this.tax.id,
              ...payload,
            })
          } else {
            await this.createTax(payload)
          }
          this.close()
        } finally {
          this.$store.commit('updateLoading', false)
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.tax-edit-modal {
  border-radius: 8px;
  max-height: 90vh;
  overflow-y: auto;

  ::v-deep .v-card {
    &__title {
      color: #1a1a1a;
    }

    &__text {
      .v-text-field,
      .v-select,
      .v-autocomplete {
        .v-input__slot {
          background-color: white;
        }
      }
    }
  }

  ::v-deep .v-radio {
    .v-label {
      width: 100%;
    }
  }

  ::v-deep .v-checkbox {
    .v-label {
      width: 100%;
    }
  }

  .disabled-section {
    opacity: 0.6;
    pointer-events: none;
  }

  .channel-mappings {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .channel-mapping-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .channel-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .direct-dot {
    background-color: #4caf50;
  }

  .airbnb-dot {
    background-color: #ff5a5f;
  }

  .booking-dot {
    background-color: #003580;
  }

  .vrbo-dot {
    background-color: #3b5998;
  }
}
</style>
